import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as registrationService from '../services/registrationService';

/**
 * useRegistration Hook
 * Manages registration state and coordinates with Supabase
 */
export const useRegistration = (registrationId = null) => {
  const { user } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [dancerInfo, setDancerInfo] = useState(null);
  const [consents, setConsents] = useState(null);
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Prevent duplicate registration creation attempts
  const isCreatingRef = useRef(false);

  /**
   * Load registration data
   */
  const loadRegistration = useCallback(async (regId) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const data = await registrationService.getCompleteRegistration(regId);

      setRegistration(data.registration);
      setDancerInfo(data.dancerInfo);
      setConsents(data.consents);
      setVariations(data.variations);
    } catch (err) {
      console.error('Error loading registration:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Create new registration (with race condition prevention)
   */
  const createRegistration = useCallback(async () => {
    if (!user) {
      const errorMsg = 'User must be logged in to create registration';
      console.error('❌', errorMsg);
      setError(errorMsg);
      return null;
    }

    // Prevent duplicate creation attempts (race condition protection)
    if (isCreatingRef.current) {
      console.log('⚠️ Registration creation already in progress, skipping duplicate call');
      return null;
    }

    try {
      isCreatingRef.current = true;
      setLoading(true);
      setError(null);

      console.log('🔄 Creating registration for user:', user.id);
      const newReg = await registrationService.createRegistration(user.id);
      console.log('✅ Registration created/retrieved successfully:', newReg);
      setRegistration(newReg);
      return newReg;
    } catch (err) {
      console.error('❌ Error creating registration:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint
      });
      setError(`Failed to create registration: ${err.message}`);
      return null;
    } finally {
      setLoading(false);
      isCreatingRef.current = false;
    }
  }, [user]);

  /**
   * Update registration record
   */
  const updateRegistrationData = useCallback(async (updates) => {
    if (!registration?.id) {
      console.error('❌ Cannot update: No active registration');
      setError('No active registration');
      return false;
    }

    try {
      setSaving(true);
      console.log('🔄 Updating registration:', registration.id, updates);

      const updated = await registrationService.updateRegistration(
        registration.id,
        updates
      );

      console.log('✅ Registration updated in DB:', updated);
      setRegistration(updated);
      return true;
    } catch (err) {
      console.error('❌ Error updating registration:', err);
      console.error('Error details:', err.message, err.code, err.details);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [registration]);

  /**
   * Save dancer information (Step 1)
   */
  const saveDancerInfo = useCallback(async (info) => {
    if (!registration?.id) {
      setError('No active registration');
      return false;
    }

    try {
      setSaving(true);
      const saved = await registrationService.saveDancerInformation(
        registration.id,
        info
      );
      setDancerInfo(saved);

      // Update registration step if needed
      if (registration.current_step < 2) {
        await updateRegistrationData({ current_step: 2 });
      }

      return true;
    } catch (err) {
      console.error('Error saving dancer info:', err);
      // The step shows an inline "couldn't save" message; a page-level
      // error here would replace the whole form.
      return false;
    } finally {
      setSaving(false);
    }
  }, [registration, updateRegistrationData]);

  /**
   * Save consent responses (Step 2)
   */
  const saveConsents = useCallback(async (consentData) => {
    if (!registration?.id) {
      setError('No active registration');
      return false;
    }

    try {
      setSaving(true);
      const saved = await registrationService.saveConsents(
        registration.id,
        consentData
      );
      setConsents(saved);

      // Update registration step if needed
      if (registration.current_step < 3) {
        await updateRegistrationData({ current_step: 3 });
      }

      return true;
    } catch (err) {
      console.error('Error saving consents:', err);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [registration, updateRegistrationData]);

  /**
   * Save variation (Step 3)
   */
  const saveVariation = useCallback(async (variationNumber, variationData) => {
    if (!registration?.id) {
      setError('No active registration');
      return false;
    }

    try {
      setSaving(true);
      const saved = await registrationService.saveVariation(
        registration.id,
        variationNumber,
        variationData
      );

      // Update variations array
      setVariations(prev => {
        const filtered = prev.filter(v => v.variation_number !== variationNumber);
        return [...filtered, saved].sort((a, b) => a.variation_number - b.variation_number);
      });

      // Update registration step if needed
      if (registration.current_step < 4) {
        await updateRegistrationData({ current_step: 4 });
      }

      return true;
    } catch (err) {
      console.error('Error saving variation:', err);
      // The step shows an inline "couldn't save" message; a page-level
      // error here would replace the whole form.
      return false;
    } finally {
      setSaving(false);
    }
  }, [registration, updateRegistrationData]);

  /**
   * Delete variation
   */
  const deleteVariation = useCallback(async (variationNumber) => {
    if (!registration?.id) return false;

    try {
      setSaving(true);
      await registrationService.deleteVariation(registration.id, variationNumber);
      setVariations(prev => prev.filter(v => v.variation_number !== variationNumber));
      return true;
    } catch (err) {
      console.error('Error deleting variation:', err);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [registration]);

  /**
   * Load registration on mount if ID provided. Otherwise end the initial
   * loading state: IndividualRegistration waits for loading === false before
   * it looks up or creates the registration, so without this it spins forever.
   */
  useEffect(() => {
    if (registrationId) {
      loadRegistration(registrationId);
    } else if (user) {
      setLoading(false);
    }
  }, [registrationId, user, loadRegistration]);

  return {
    // State
    registration,
    dancerInfo,
    consents,
    variations,
    loading,
    error,
    saving,

    // Actions
    createRegistration,
    loadRegistration,
    updateRegistrationData,
    saveDancerInfo,
    saveConsents,
    saveVariation,
    deleteVariation,

    // Computed values
    currentStep: registration?.current_step || 1,
    isDraft: registration?.status === 'draft',
    isComplete: registration?.status === 'complete'
  };
};
